import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  FormControl,
  MenuItem,
  Select,
  Chip,
  Checkbox,
  ListItemText,
  TextField,
  Typography,
  IconButton,
} from "@mhome/ui";
import MTextField from "../common/text-field";
import CloseIcon from "@mui/icons-material/Close";
import DataObjectIcon from "@mui/icons-material/DataObject";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { useIsSmallScreen } from "../common/adaptive-theme-provider";

// MenuProps removed - not needed for custom Select component

const EntityFilter = ({
  sortBy,
  setSortBy,
  setFilterText,
  meta,
  selectedIntegrations,
  setSelectedIntegrations,
  selectedCapabilities,
  setSelectedCapabilities,
  entities,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const isSmallScreen = useIsSmallScreen();

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  const handleSearchInputChange = (event) => {
    console.debug(event.target.value);
    setSearchInput(event.target.value);
    setFilterText(event.target.value);
  };

  const handleClear = () => {
    setSearchInput("");
    setFilterText("");
  };

  const handleIntegrationChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedIntegrations(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleCapabilityChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedCapabilities(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleRemoveIntegration = (integrationToRemove) => {
    setSelectedIntegrations(
      selectedIntegrations.filter(
        (integration) => integration !== integrationToRemove
      )
    );
  };

  const handleRemoveCapability = (capabilityToRemove) => {
    setSelectedCapabilities(
      selectedCapabilities.filter(
        (capability) => capability !== capabilityToRemove
      )
    );
  };

  // Get unique integrations from meta that have at least one entity
  const availableIntegrations = useMemo(() => {
    if (!meta || !meta.integrationNames || !entities) return [];

    // Create a set of all integrations that have at least one entity
    const integrationsWithEntities = new Set();
    entities.forEach((entity) => {
      if (entity.integration) {
        integrationsWithEntities.add(entity.integration);
      }
    });

    // Return only the integrations that have entities
    return Object.keys(meta.integrationNames).filter((integration) =>
      integrationsWithEntities.has(integration)
    );
  }, [meta, entities]);

  // Get unique capabilities from entities
  const availableCapabilities = useMemo(() => {
    if (!entities || !meta || !meta.capabilityNames) return [];

    // Create a set of all capabilities that have at least one entity
    const capabilitiesWithEntities = new Set();
    entities.forEach((entity) => {
      if (entity.capabilities && Array.isArray(entity.capabilities)) {
        entity.capabilities.forEach((capability) => {
          capabilitiesWithEntities.add(capability);
        });
      }
    });

    return Array.from(capabilitiesWithEntities).sort();
  }, [entities, meta]);

  // Get capability display name from meta
  const getCapabilityDisplayName = (capability) => {
    return meta?.capabilityNames?.[capability] || capability;
  };

  // Determine if a capability is a data type (state/event) or operation type (command)
  const getCapabilityType = (capability) => {
    // This is a simplified logic - you may need to adjust based on your actual data structure
    const lowerCapability = capability.toLowerCase();

    // Check for common data-related keywords
    if (
      lowerCapability.includes("state") ||
      lowerCapability.includes("event") ||
      lowerCapability.includes("data") ||
      lowerCapability.includes("property") ||
      lowerCapability.includes("attribute")
    ) {
      return "data";
    }

    // Check for common operation-related keywords
    if (
      lowerCapability.includes("command") ||
      lowerCapability.includes("action") ||
      lowerCapability.includes("operation") ||
      lowerCapability.includes("function") ||
      lowerCapability.includes("method")
    ) {
      return "operation";
    }

    // Default to data if we can't determine
    return "data";
  };

  return (
    <Card
      className="mb-3 w-full"
      elevation={0}
      style={{
        backgroundColor: getComponentBgColor("hsl(var(--card))"),
      }}
    >
      <div className="flex flex-col p-2 w-full gap-3">
        <div
          className={`flex ${isSmallScreen ? "flex-col" : "flex-row"} gap-3`}
        >
          {/* Name filter */}
          <div className="flex-1">
            <Typography
              variant="caption"
              color="textSecondary"
              className="font-medium mb-1 block"
            >
              Name
            </Typography>
            <TextField
              value={searchInput}
              onChange={handleSearchInputChange}
              placeholder="Filter by name"
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                endAdornment: searchInput && (
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    className="-mr-1"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </div>

          {/* Provider filter */}
          <div className="flex-1">
            <Typography
              variant="caption"
              color="textSecondary"
              className="font-medium mb-1 block"
            >
              Provider
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={selectedIntegrations}
                onChange={handleIntegrationChange}
                renderValue={(selected) => (
                  <div className="flex flex-wrap gap-1">
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={meta?.integrationNames?.[value] || value}
                        size="small"
                        className="h-6"
                      />
                    ))}
                  </div>
                )}
              >
                {availableIntegrations.map((integration) => (
                  <MenuItem key={integration} value={integration}>
                    <Checkbox
                      checked={selectedIntegrations.indexOf(integration) > -1}
                      className="mr-2 p-1"
                    />
                    <ListItemText
                      primary={
                        meta?.integrationNames?.[integration] || integration
                      }
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Capability filter */}
          <div className="flex-1">
            <Typography
              variant="caption"
              color="textSecondary"
              className="font-medium mb-1 block"
            >
              Capability
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                multiple
                value={selectedCapabilities}
                onChange={handleCapabilityChange}
                renderValue={(selected) => (
                  <div className="flex flex-wrap gap-1">
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={getCapabilityDisplayName(value)}
                        size="small"
                        className="h-6"
                        icon={
                          getCapabilityType(value) === "data" ? (
                            <DataObjectIcon fontSize="small" />
                          ) : (
                            <PlayArrowIcon fontSize="small" />
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              >
                {availableCapabilities.map((capability) => {
                  const capabilityType = getCapabilityType(capability);
                  return (
                    <MenuItem key={capability} value={capability}>
                      <Checkbox
                        checked={selectedCapabilities.indexOf(capability) > -1}
                        className="mr-2 p-1"
                      />
                      <div className="flex items-center">
                        {capabilityType === "data" ? (
                          <DataObjectIcon
                            fontSize="small"
                            className="mr-2"
                            style={{ color: "hsl(var(--muted-foreground))" }}
                          />
                        ) : (
                          <PlayArrowIcon
                            fontSize="small"
                            className="mr-2"
                            style={{ color: "hsl(var(--muted-foreground))" }}
                          />
                        )}
                        <ListItemText
                          primary={getCapabilityDisplayName(capability)}
                          secondary={
                            capabilityType === "data"
                              ? "Data (State/Event)"
                              : "Operation (Command)"
                          }
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </div>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default EntityFilter;
